import { computed, reactive, ref } from 'vue';
import type { Ref } from 'vue';
import axios from 'axios';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { useFeeRecipientsStore } from '@/stores/useFeeRecipientsStore';
import { useVendorSearch } from './useVendorSearch';
import type { VendorData } from '@/models/lienTypes';
import type {
  MatterRecipientData as MatterRecipient,
  FeeRecipientRole,
} from '@/models/settlementTypes';

/** VR-001 — enforced server-side too; this only drives the disabled/tooltip UX. */
export const MAX_FEE_RECIPIENTS = 5;

/** How long to wait after the last keystroke/select before autosaving an existing row. */
export const AUTOSAVE_DEBOUNCE_MS = 600;

/** Row-error key for the not-yet-created inline "Add Recipient" row. */
const NEW_ROW_ID = '__new__';

/** Blocks the save like any other validation failure, but is surfaced as the table-level
 *  "percentages exceed 100%" banner rather than an inline row error — the overage is a property of
 *  the whole table, not of the one row that happened to tip it over. */
export const PERCENTAGE_SUM_ERROR = 'Total percentage cannot exceed 100%';

/** Snackbar copy for the same rule, phrased as the direct instruction the user needs. */
export const PERCENTAGE_SUM_SNACKBAR = 'Percentages exceed 100% — please adjust';

interface RecipientDraft {
  vendorId: string | null;
  role: FeeRecipientRole;
  percentage: number;
}

function apiErrorMessage(e: unknown): string | undefined {
  return axios.isAxiosError(e) ? e.response?.data?.message : undefined;
}

function apiErrorStatus(e: unknown): number | undefined {
  return axios.isAxiosError(e) ? e.response?.status : undefined;
}

/**
 * The backend rejects edits with a 400 carrying this message once the settlement has advanced past
 * the point where recipients may change. It means our `isLocked` snapshot is simply stale — the
 * right response is to re-sync and render the row read-only, not to park an error message beside a
 * field the user is no longer allowed to edit.
 */
function isLockedError(e: unknown): boolean {
  return (
    apiErrorStatus(e) === 400 &&
    /locked while a settlement is in progress/i.test(apiErrorMessage(e) ?? '')
  );
}

function draftOf(recipient: MatterRecipient): RecipientDraft {
  return { vendorId: recipient.vendorId, role: recipient.role, percentage: recipient.percentage };
}

/**
 * Every existing recipient row is always live-editable (matches the mockup — no separate
 * "click to edit" step): each field change validates immediately and, once valid, autosaves in
 * the background after `AUTOSAVE_DEBOUNCE_MS` of no further changes to that row. Rows autosave
 * independently of each other. The not-yet-created Add row is the one exception — it only saves
 * on explicit ✓, to avoid `POST`-ing a duplicate recipient on every keystroke.
 *
 * `confirmRemoveResetDialog`/`confirmEditResetDialog` are injected (rather than owned here) so
 * this stays presentation-agnostic and testable, same pattern as `MatterLiensTab.vue`.
 */
export function useInlineRecipientEditor(
  matterId: Ref<string | null>,
  recipients: Ref<MatterRecipient[]>,
  confirmRemoveResetDialog: () => Promise<boolean>,
  confirmEditResetDialog: () => Promise<boolean>,
) {
  const store = useFeeRecipientsStore();
  const { handleError, handleSuccess, handleWarning } = useSnackbarHandler();

  /** Keyed by `matterSettlementRecipientId`; lazily seeded from the recipient on first render. */
  const rowDrafts = reactive<Record<string, RecipientDraft>>({});
  /** Ids currently mid-autosave (background PATCH in flight). */
  const savingIds = ref<Set<string>>(new Set());
  /** Inline error keyed by row id (`NEW_ROW_ID` for the add row, else `matterSettlementRecipientId`). */
  const rowErrors = ref<Record<string, string>>({});

  const vendorSearchesById = new Map<string, ReturnType<typeof useVendorSearch>>();
  const autosaveTimers = new Map<string, ReturnType<typeof setTimeout>>();

  const canAddMore = computed(() => recipients.value.length < MAX_FEE_RECIPIENTS);

  function clearRowError(id: string): void {
    if (id in rowErrors.value) {
      const next = { ...rowErrors.value };
      delete next[id];
      rowErrors.value = next;
    }
  }

  function clearAutosaveTimer(id: string): void {
    const timer = autosaveTimers.get(id);
    if (timer) {
      clearTimeout(timer);
      autosaveTimers.delete(id);
    }
  }

  function setSaving(id: string, isSaving: boolean): void {
    const next = new Set(savingIds.value);
    if (isSaving) next.add(id);
    else next.delete(id);
    savingIds.value = next;
  }

  /** The live-editable values for an existing row — mutate directly, then call `onFieldChange`. */
  function draftFor(recipient: MatterRecipient): RecipientDraft {
    const id = recipient.matterSettlementRecipientId;
    if (!rowDrafts[id]) rowDrafts[id] = draftOf(recipient);
    return rowDrafts[id];
  }

  function vendorSearchFor(recipient: MatterRecipient) {
    const id = recipient.matterSettlementRecipientId;
    let vendorSearch = vendorSearchesById.get(id);
    if (!vendorSearch) {
      vendorSearch = useVendorSearch();
      vendorSearch.search(''); // preload so the dropdown isn't empty on first open
      vendorSearchesById.set(id, vendorSearch);
    }
    return vendorSearch;
  }

  /** Vendor picker options for one row, minus any vendor already assigned to a *different* row.
   *  The row's own current vendor is always prepended if missing from the search results, so it
   *  renders as selected right away. */
  function availableVendorsFor(recipient: MatterRecipient): VendorData[] {
    const id = recipient.matterSettlementRecipientId;
    const usedByOthers = new Set(
      recipients.value.filter((r) => r.matterSettlementRecipientId !== id).map((r) => r.vendorId),
    );
    const fromSearch = vendorSearchFor(recipient).vendors.value.filter((v) => !usedByOthers.has(v.vendorId));
    const currentVendorId = draftFor(recipient).vendorId;
    if (!currentVendorId || fromSearch.some((v) => v.vendorId === currentVendorId)) return fromSearch;
    return [{ vendorId: currentVendorId, displayName: recipient.vendorDisplayName }, ...fromSearch];
  }

  function isSaving(recipient: MatterRecipient): boolean {
    return savingIds.value.has(recipient.matterSettlementRecipientId);
  }

  /** UX-only mirror of VR-001/VR-002/VR-004 — the server re-validates authoritatively regardless. */
  function validateDraft(id: string, draft: RecipientDraft): string | null {
    if (!draft.vendorId) return 'Select a vendor';
    if (!(draft.percentage > 0)) return 'Percentage must be greater than 0';
    const sumOfOthers = recipients.value
      .filter((r) => r.matterSettlementRecipientId !== id)
      .reduce((sum, r) => sum + r.percentage, 0);
    if (sumOfOthers + draft.percentage > 100) return PERCENTAGE_SUM_ERROR;
    return null;
  }

  /** The live percentage for a row — the draft's if one has been touched, else the saved value.
   *  Deliberately does NOT seed `rowDrafts` the way `draftFor` does, so callers can total this up
   *  inside a computed without writing to reactive state during evaluation. */
  function percentageFor(recipient: MatterRecipient): number {
    return rowDrafts[recipient.matterSettlementRecipientId]?.percentage ?? recipient.percentage;
  }

  /** Live total across every row — an over-limit edit is never persisted, so summing the saved
   *  percentages instead would never actually register the overage. */
  const totalPercentage = computed(() =>
    recipients.value.reduce((sum, r) => sum + (percentageFor(r) || 0), 0),
  );
  const isOverAllocated = computed(() => totalPercentage.value > 100);

  /** Rising-edge latch for the over-100% snackbar, updated in `onFieldChange`. Deliberately not a
   *  `watch` on `isOverAllocated`: watching would keep `totalPercentage` eagerly evaluated, so it
   *  would reduce over `recipients` from inside unrelated effect flushes and could observe the
   *  array mid-replacement. Left lazy, it is only ever evaluated during render. */
  let wasOverAllocated = false;

  /** Call on every Vendor/Role/% change to an existing row — validates immediately (showing an
   *  inline error and skipping the network call if invalid) and, once valid, (re)starts the
   *  debounce window before autosaving. */
  function onFieldChange(recipient: MatterRecipient): void {
    const id = recipient.matterSettlementRecipientId;
    clearAutosaveTimer(id);
    const draft = rowDrafts[id];
    if (!draft) return;
    const validationError = validateDraft(id, draft);

    // Announce the overage once, on the edit that crosses 100% — re-notifying on every keystroke
    // while still over would bury the user. The inline hint carries the state until it clears.
    const isOver = validationError === PERCENTAGE_SUM_ERROR;
    if (isOver && !wasOverAllocated) handleWarning(PERCENTAGE_SUM_SNACKBAR);
    wasOverAllocated = isOver;

    if (validationError) {
      // The sum overage is table-level (snackbar + hint); anything else is specific to this row.
      if (isOver) clearRowError(id);
      else rowErrors.value = { ...rowErrors.value, [id]: validationError };
      return;
    }
    clearRowError(id);
    autosaveTimers.set(
      id,
      setTimeout(() => {
        autosaveTimers.delete(id);
        persistExisting(recipient);
      }, AUTOSAVE_DEBOUNCE_MS),
    );
  }

  /** The actual PATCH call, shared by the first attempt and the confirmed retry. */
  function sendUpdate(id: string, recipient: MatterRecipient, draft: RecipientDraft, confirmReset: boolean) {
    return store.updateRecipient(
      matterId.value!,
      id,
      {
        vendorId: draft.vendorId!, // required by the backend even though unchanged — see UpdateMatterRecipientRequest
        role: draft.role,
        percentage: draft.percentage,
        sortOrder: recipient.sortOrder,
      },
      confirmReset,
    );
  }

  /**
   * Changing vendor or role on a row with an already-confirmed Step 2 expense resets the
   * settlement (sc-45849) — same VR-013-style tiering as Delete: first call omits `confirmReset`,
   * a `409` means show the confirm dialog, then retry with `confirmReset: true`. Percentage-only
   * changes never trigger this (the backend only checks vendor/role), so most autosaves skip
   * straight to the success path.
   */
  async function persistExisting(recipient: MatterRecipient): Promise<void> {
    const id = recipient.matterSettlementRecipientId;
    const draft = rowDrafts[id];
    if (!draft || !matterId.value) return;
    setSaving(id, true);
    try {
      let updated: MatterRecipient;
      try {
        updated = await sendUpdate(id, recipient, draft, false);
      } catch (e: unknown) {
        if (apiErrorStatus(e) !== 409) throw e;
        const confirmed = await confirmEditResetDialog();
        if (!confirmed) {
          rowDrafts[id] = draftOf(recipient); // revert to the last saved value, nothing was applied
          return;
        }
        updated = await sendUpdate(id, recipient, draft, true);
      }
      // Sync this row's draft from the response right away (e.g. a rounded percentage) rather
      // than leaving the input showing what was typed.
      if (!autosaveTimers.has(id)) rowDrafts[id] = draftOf(updated);
      // Then re-fetch the whole table — the PATCH response can itself echo back an unrounded
      // value, so every row (including this one) gets a second, more authoritative sync from
      // the GET's canonical values, rather than trusting the mutation's own response to be final.
      await store.fetchRecipients(matterId.value);
      for (const r of recipients.value) {
        const rid = r.matterSettlementRecipientId;
        if (!autosaveTimers.has(rid)) rowDrafts[rid] = draftOf(r);
      }
      clearRowError(id);
      handleSuccess('Changes saved');
    } catch (e: unknown) {
      if (isLockedError(e)) {
        await syncLockedState();
        return;
      }
      rowErrors.value = {
        ...rowErrors.value,
        [id]: apiErrorMessage(e) ?? 'Unable to save this recipient.',
      };
    } finally {
      setSaving(id, false);
    }
  }

  /** Re-reads the server's truth after a lock rejection: `isLocked` flips to true (switching every
   *  row to read-only text) and each draft is reset to the persisted value, discarding the edit
   *  that can no longer be applied. */
  async function syncLockedState(): Promise<void> {
    if (!matterId.value) return;
    autosaveTimers.forEach((timer) => clearTimeout(timer));
    autosaveTimers.clear();
    rowErrors.value = {};
    await store.fetchRecipients(matterId.value);
    for (const r of recipients.value) {
      rowDrafts[r.matterSettlementRecipientId] = draftOf(r);
    }
  }

  // --- Add row — the one flow that still needs an explicit ✓, to avoid POSTing on every keystroke ---

  const isAdding = ref(false);
  const addDraft = ref<RecipientDraft | null>(null);
  const addSaving = ref(false);
  const addVendorSearch = useVendorSearch();

  const availableVendorsForAdd = computed<VendorData[]>(() => {
    const usedByOthers = new Set(recipients.value.map((r) => r.vendorId));
    return addVendorSearch.vendors.value.filter((v) => !usedByOthers.has(v.vendorId));
  });

  function startAdd(): void {
    if (isAdding.value || !canAddMore.value) return;
    isAdding.value = true;
    addDraft.value = { vendorId: null, role: 'coCounsel', percentage: 0 };
    clearRowError(NEW_ROW_ID); // don't reopen showing a stale error from a previous attempt
    addVendorSearch.reset();
    addVendorSearch.search(''); // preload vendors (mirrors LienFormPanel) so the dropdown isn't empty
  }

  function cancelAdd(): void {
    isAdding.value = false;
    addDraft.value = null;
    clearRowError(NEW_ROW_ID);
    addVendorSearch.reset();
  }

  /** Call on every Vendor/Role/% change in the add row. Unlike an existing row, the add row has
   *  no autosave to re-validate it, so without this a message raised by a failed ✓ ("Select a
   *  vendor") would stay on screen after the user fixed the very thing it complained about, and
   *  only clear on the next ✓ or Cancel.
   *
   *  Deliberately re-validates only while a message is already showing: before the first ✓ the
   *  row is untouched, and an empty draft shouldn't start yelling as soon as it's opened. */
  function onAddFieldChange(): void {
    if (!addDraft.value || !rowErrors.value[NEW_ROW_ID]) return;
    const validationError = validateDraft(NEW_ROW_ID, addDraft.value);
    if (validationError) rowErrors.value = { ...rowErrors.value, [NEW_ROW_ID]: validationError };
    else clearRowError(NEW_ROW_ID);
  }

  async function saveAdd(): Promise<void> {
    if (!addDraft.value || !matterId.value) return;
    const validationError = validateDraft(NEW_ROW_ID, addDraft.value);
    if (validationError) {
      rowErrors.value = { ...rowErrors.value, [NEW_ROW_ID]: validationError };
      return;
    }
    addSaving.value = true;
    try {
      await store.createRecipient(matterId.value, {
        vendorId: addDraft.value.vendorId!,
        role: addDraft.value.role,
        percentage: addDraft.value.percentage,
        sortOrder: recipients.value.length,
      });
    } catch (e: unknown) {
      // Creation itself failed — nothing was saved, so keep the add row open with an inline
      // error the same way every other validation/API failure on this row is surfaced.
      rowErrors.value = {
        ...rowErrors.value,
        [NEW_ROW_ID]: apiErrorMessage(e) ?? 'Unable to add this recipient.',
      };
      addSaving.value = false;
      return;
    }
    addSaving.value = false;
    // The new row is already in `recipients` (store appends it optimistically) — clear the add
    // placeholder now so it doesn't render alongside the real row while the re-fetch below is
    // still in flight (that gap used to show as a duplicate/blank row that vanished a beat later).
    cancelAdd();
    handleSuccess('Recipient added');
    try {
      // The POST response echoes back the unrounded percentage exactly as typed, the same way the
      // PATCH response does — so a `4.123456` entry would sit in the new row at full precision
      // until the next page load, while the backend has already stored the rounded 4dp value.
      // Re-fetch for the canonical figures and re-seed every draft from them, mirroring the second
      // sync in `persistExisting`. Rows with an autosave still pending keep their in-progress
      // draft, so this never clobbers an edit the user is midway through on another row.
      await store.fetchRecipients(matterId.value);
      for (const r of recipients.value) {
        const rid = r.matterSettlementRecipientId;
        if (!autosaveTimers.has(rid)) rowDrafts[rid] = draftOf(r);
      }
    } catch (e: unknown) {
      // The recipient was already created and the add row is already closed — there's no row
      // left to attach an inline error to, so surface this as a toast instead of silently
      // dropping it. The new row still shows (at full, unrounded precision) until the next load.
      handleError(e);
    }
  }

  /**
   * VR-013's three tiers share one `DELETE` call, read off the response:
   * free tier → `204` immediately; reset tier → first call `409` → confirm
   * dialog → retry with `confirmReset=true` → `204`; hard-block tier (locked)
   * → `400`, fallback-only since Remove is hidden while locked.
   */
  async function remove(recipient: MatterRecipient): Promise<void> {
    if (!matterId.value) return;
    const id = recipient.matterSettlementRecipientId;
    clearRowError(id);
    try {
      await store.deleteRecipient(matterId.value, id);
      cleanupRow(id);
      handleSuccess('Recipient removed');
    } catch (e: unknown) {
      const status = apiErrorStatus(e);
      if (status === 409) {
        const confirmed = await confirmRemoveResetDialog();
        if (!confirmed) return;
        try {
          await store.deleteRecipient(matterId.value, id, true);
          cleanupRow(id);
          handleSuccess('Recipient removed');
        } catch (retryError: unknown) {
          handleError(retryError);
        }
      } else if (isLockedError(e)) {
        // Remove is hidden while locked, so reaching here means our snapshot was stale — re-sync
        // and let the row render read-only rather than showing an error on a hidden control.
        await syncLockedState();
      } else if (status === 400) {
        rowErrors.value = {
          ...rowErrors.value,
          [id]: apiErrorMessage(e) ?? 'Unable to remove this recipient.',
        };
      } else {
        handleError(e);
      }
    }
  }

  function cleanupRow(id: string): void {
    clearAutosaveTimer(id);
    clearRowError(id);
    delete rowDrafts[id];
    vendorSearchesById.delete(id);
  }

  return {
    rowErrors,
    canAddMore,
    draftFor,
    percentageFor,
    totalPercentage,
    isOverAllocated,
    vendorSearchFor,
    availableVendorsFor,
    isSaving,
    onFieldChange,
    isAdding,
    addDraft,
    addSaving,
    addVendorSearch,
    availableVendorsForAdd,
    startAdd,
    cancelAdd,
    onAddFieldChange,
    saveAdd,
    remove,
  };
}
